import * as signalr from '@microsoft/signalr';
import { useEffect, useRef } from 'react';

import type { ICouponBroadcastMessage } from '@/components/coupon/held-coupons';
import { SIGNALRAPIS } from '@/constants/global';

/**
 * Subscribes to the wallet-service coupon hub and hands every `CouponBroadcast` to `onArrival`.
 *
 * Each arrival is delivered, not stored: the previous version held one coupon in state, so a
 * second broadcast replaced the first before anyone saw it. The callback is read through a ref,
 * so a caller passing a fresh function each render does not tear down the connection.
 */
export const useCouponBroadcast = (onArrival: (message: ICouponBroadcastMessage) => void) => {
	const onArrivalRef = useRef(onArrival);
	onArrivalRef.current = onArrival;

	useEffect(() => {
		let mounted = true;
		let connection: signalr.HubConnection;

		try {
			connection = new signalr.HubConnectionBuilder()
				.withUrl(SIGNALRAPIS.COUPONS, {
					skipNegotiation: true,
					transport: signalr.HttpTransportType.WebSockets,
				})
				.withAutomaticReconnect()
				.configureLogging(signalr.LogLevel.None)
				.build();
		} catch {
			return;
		}

		connection.on('CouponBroadcast', (data: ICouponBroadcastMessage) => {
			if (mounted) onArrivalRef.current(data);
		});

		connection.start().catch(() => {
			// Wallet service unreachable — silently degrade; no coupons arrive this session.
		});

		return () => {
			mounted = false;
			connection.stop().catch(() => {});
		};
	}, []);
};
